import { BtnGroup, BtnToolbar, Button, ButtonAction } from '@dec/shared';
import { label as labelCommon } from 'app/modules/translation';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ConfirmDialog } from '@/app/core/dialog';
import { useSpinnerActions } from '@/app/layouts/store';
import { type CatalogValue } from '@/app/modules/configuration/catalogs/models/Catalog';
import { type IdValue } from '@/app/modules/TaxStatement/FormVersion/types';

import { type PaymentDeclarationContent } from '../../models/Payment';
import { paymentService } from '../../services/payment.service';

import { PaymentFormContent } from './paymentFormContent';

interface PaymentFormProps {
  id: string;
}

interface TaxReturnPaymentForm {
  paymentMethod: CatalogValue;
  paymentDate: Date;
  currency: CatalogValue;
  amount: number;
  swift: string;
  reference: string;
  attachment?: {
    id: string;
  };
}

export function PaymentForm({ id }: PaymentFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showSpinner, hideSpinner } = useSpinnerActions();

  const onCancel = () => {
    navigate(-1);
  };

  const onSubmit = async (data: TaxReturnPaymentForm) => {
    const confirmed = await ConfirmDialog({
      titleModal: t('common.dialog.warning-title'),
      message: t('common.messages.confirm'),
      okButton: t('taxpayer-payments.title.pagePayments'),
      cancelButton: labelCommon('cancel')
    });

    if (!confirmed) {
      return;
    }

    const request: PaymentDeclarationContent = {
      taxReturnId: id,
      amount: data.amount,
      paymentMethodId: data.paymentMethod.id!,
      paymentReference: data.reference,
      attachmentId: data?.attachment?.id,
      paymentDate: data?.paymentDate,
      currencyId: data.currency.id!,
      swift: data.swift
    };

    showSpinner();
    try {
      const response: IdValue = await paymentService.createPaymentDeclaration(request);

      navigate(`/taxpayer/taxreturn-payment/${response.id}/success`);
    } finally {
      hideSpinner();
    }
  };

  return (
    <>
      <PaymentFormContent nameForm="payment" onSubmit={onSubmit} />

      <BtnToolbar>
        <BtnGroup>
          <Button action={ButtonAction.cancel} variant="secondary" onClick={onCancel} />
        </BtnGroup>
        <BtnGroup>
          <Button
            action={ButtonAction.save}
            text={t('taxpayer-payments.title.pagePayments')}
            type="submit"
            variant="primary"
            form="payment"
          />
        </BtnGroup>
      </BtnToolbar>
    </>
  );
}
